import { ArrowLongDownIcon, ArrowLongUpIcon } from '@heroicons/react/24/outline';
import React, { useRef, useState } from 'react';
import { useTable, useFilters, useGlobalFilter, useAsyncDebounce, useSortBy } from 'react-table'
import { ExportToCsv } from 'export-to-csv';
import Button from './button';
import ReactToPrint from 'react-to-print';

const TWO_HUNDRED_MS = 200;

function GlobalFilter({
    preGlobalFilteredRows,
    globalFilter,
    setGlobalFilter,
}) {
    const [value, setValue] = useState(globalFilter);
    const onChange = useAsyncDebounce(value => {
        setGlobalFilter(value || undefined)
    }, TWO_HUNDRED_MS);

    return (
        <div className="block">
            <input
                value={value || ""}
                onChange={e => {
                    setValue(e.target.value);
                    onChange(e.target.value);
                }}
                className="p-2 my-3 rounded-md border border-neutral-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder={`Search...`}
            />
        </div>

    )
}

export const ComponentToPrint = React.forwardRef((props, ref) => {
    let { columns, data } = props;
    const tableInstance = useTable({ columns, data });
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow
    } = tableInstance;
    return (
        <div ref={ref} className="m-10">
            <style type="text/css" media="print">
         {" @page { size: landscape; } "}
       </style>
            <table className="min-w-full divide-y divide-neutral-200" {...getTableProps()}>
                <thead>
                    {
                        headerGroups.map(headerGroup => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {
                                    headerGroup.headers.map(column => (
                                        <th {...column.getHeaderProps()} className="bg-neutral-100 px-6 py-3 text-left text-sm font-semibold text-neutral-900">
                                            {
                                                column.render('Header')}
                                            <span>
                                                {column.isSorted
                                                    ? column.isSortedDesc
                                                        ? <ArrowLongDownIcon className="inline w-3 h-3 text-neutral-700"></ArrowLongDownIcon>
                                                        : <ArrowLongUpIcon className="inline w-3 h-3 text-neutral-700"></ArrowLongUpIcon>
                                                    : ''}
                                            </span>
                                        </th>
                                    ))}
                            </tr>
                        ))}
                </thead>
                <tbody className="divide-y divide-neutral-200 bg-white"  {...getTableBodyProps()}>
                    {
                        rows.map(row => {
                            prepareRow(row)
                            return (
                                <tr {...row.getRowProps()} className="bg-white">
                                    {
                                        row.cells.map(cell => {
                                            return (
                                                <td {...cell.getCellProps()} className="whitespace-nowrap px-6 py-4 text-sm text-neutral-900">
                                                    {
                                                        cell.render('Cell')}
                                                </td>
                                            )
                                        })}
                                </tr>
                            )
                        })}
                </tbody>
            </table>
        </div>
    );
});

export default function Table({ columns, data, exportData, exportColumns }) {
    const componentRef = useRef();
    const tableInstance = useTable({ columns, data }, useGlobalFilter, useSortBy);
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        state: { globalFilter },
        preGlobalFilteredRows,
        setGlobalFilter
    } = tableInstance;

    const exportToCsv = () => {
        const options = {
            fieldSeparator: ',',
            quoteStrings: '"',
            decimalSeparator: '.',
            showLabels: true,
            useTextFile: false,
            useBom: true,
            headers: exportColumns
        };

        const csvExporter = new ExportToCsv(options);

        csvExporter.generateCsv(exportData);
    }

    return <>
        <div className="flex justify-between">
            <GlobalFilter
                preGlobalFilteredRows={preGlobalFilteredRows}
                globalFilter={globalFilter}
                setGlobalFilter={setGlobalFilter}
            />

            <div>
                {exportData && <Button onClick={() => exportToCsv()} text="Export To CSV"></Button>}
                {exportData && <ReactToPrint trigger={() => {
                    return <Button className="ml-5" text="Print"></Button>
                }} content={() => componentRef.current} />}
                {exportData && <div className="hidden"><ComponentToPrint ref={componentRef} data={data} columns={columns.filter(x => exportColumns.includes('' + x.Header))}></ComponentToPrint></div>}
            </div>

        </div>

        <div className="min-w-full overflow-hidden overflow-x-auto align-middle shadow sm:rounded-lg">
            <div className="min-w-full overflow-hidden overflow-x-auto align-middle shadow sm:rounded-lg">

                <table className="min-w-full divide-y divide-neutral-200" {...getTableProps()}>
                    <thead>
                        {
                            headerGroups.map(headerGroup => (
                                <tr {...headerGroup.getHeaderGroupProps()}>
                                    {
                                        headerGroup.headers.map(column => (
                                            <th {...column.getHeaderProps(column.getSortByToggleProps())} className="bg-neutral-100 px-6 py-3 text-left text-sm font-semibold text-neutral-900">
                                                {
                                                    column.render('Header')}
                                                <span>
                                                    {column.isSorted
                                                        ? column.isSortedDesc
                                                            ? <ArrowLongDownIcon className="inline w-3 h-3 text-neutral-700"></ArrowLongDownIcon>
                                                            : <ArrowLongUpIcon className="inline w-3 h-3 text-neutral-700"></ArrowLongUpIcon>
                                                        : ''}
                                                </span>
                                            </th>
                                        ))}
                                </tr>
                            ))}
                    </thead>
                    <tbody className="divide-y divide-neutral-200 bg-white"  {...getTableBodyProps()}>
                        {
                            rows.map(row => {
                                prepareRow(row)
                                return (
                                    <tr {...row.getRowProps()} className="bg-white">
                                        {
                                            row.cells.map(cell => {
                                                return (
                                                    <td {...cell.getCellProps()} className="whitespace-nowrap px-6 py-4 text-sm text-neutral-900">
                                                        {
                                                            cell.render('Cell')}
                                                    </td>
                                                )
                                            })}
                                    </tr>
                                )
                            })}
                    </tbody>
                </table>


            </div>
        </div>
    </>

}